import { HandledError } from 'fitbeat.models';
import { DisplayableUserProfile, IDisplayableUserProfile, UserProfile } from '../../entities';
import { IUserProfileRepository } from '../../persistence';
import { IPartnersManager } from './ipartnersmanager';

export class PartnersManager implements IPartnersManager {
    constructor(
        private userProfileRepository: IUserProfileRepository,
    ) { }
    public async addPartner(user: string | UserProfile, partner: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        user.partnerWhitelist = await this.addToList(partner, user.partnerWhitelist);
        user.partnerBlacklist = await this.removeFromList(partner.uid, user.partnerBlacklist);
        return this.userProfileRepository.update(user);
    }
    public async getPartners(user: string | UserProfile): Promise<IDisplayableUserProfile[]> {
        user = await this.getUserOrError(user);
        return user.partnerWhitelist ? user.partnerWhitelist : [];
    }
    public async removePartner(user: string | UserProfile, partner: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        user.partnerWhitelist = this.removeFromList(partner.uid, user.partnerWhitelist);
        return this.userProfileRepository.update(user);
    }
    public async blockPartner(user: string | UserProfile, partner: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        user.partnerBlacklist = await this.addToList(partner, user.partnerBlacklist);
        user.partnerWhitelist = await this.removeFromList(partner.uid, user.partnerWhitelist);
        return this.userProfileRepository.update(user);
    }
    public async unblockPartner(user: string | UserProfile, partner: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        user.partnerBlacklist = this.removeFromList(partner.uid, user.partnerBlacklist);
        await this.addPartner(user, partner);
        return this.userProfileRepository.update(user);
    }
    public async getBlockedPartners(user: string | UserProfile): Promise<IDisplayableUserProfile[]> {
        user = await this.getUserOrError(user);
        return user.partnerBlacklist ? user.partnerBlacklist : [];
    }

    public async setProfilePrivate(user: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        user.privateProfile = true;
        return this.userProfileRepository.update(user);
    }

    public async setProfilePublic(user: string | UserProfile): Promise<UserProfile> {
        user = await this.getUserOrError(user);
        user.privateProfile = false;
        return this.userProfileRepository.update(user);
    }

    public async isInBlacklist(user: string | UserProfile, partner: string | UserProfile): Promise<boolean> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        return this.isInList(partner.uid, user.partnerBlacklist);
    }

    public async isInWhitelist(user: string | UserProfile, partner: string | UserProfile): Promise<boolean> {
        user = await this.getUserOrError(user);
        partner = await this.getUserOrError(partner);
        return this.isInList(partner.uid, user.partnerWhitelist);
    }

    public async isPrivate(user: string | UserProfile, partnerId?: string): Promise<boolean> {
        user = await this.getUserOrError(user);
        if (partnerId && user.partnerWhitelist) {
            // if the user being checked for has added the partner doing the check to their whitelist, then
            // for this user's profile is not private to the user doing the check
            if (this.isInList(partnerId, user.partnerWhitelist)) {
                return false;
            }
        }
        return user.privateProfile;
    }

    public async searchForPartner(user: string | UserProfile, usernameOrFullnameOrNickname: string)
        : Promise<IDisplayableUserProfile[]> {
        const userProfile: UserProfile = await this.getUserOrError(user);
        const userProfiles = await this.userProfileRepository.search(usernameOrFullnameOrNickname);

        return userProfiles.filter((profile: UserProfile) => {
            return userProfile.uid !== profile.uid;
        }).map((profile: UserProfile) => {
            return this.transformToDisplayableUserProfile(profile);
        });
    }

    private removeFromList(userId: string, list?: IDisplayableUserProfile[]): IDisplayableUserProfile[] {
        const tempList = Object.assign([], list);
        return tempList.filter((user: IDisplayableUserProfile) => user.uid !== userId);
    }

    private async addToList(user: string | UserProfile, list?: IDisplayableUserProfile[])
        : Promise<IDisplayableUserProfile[]> {
        const tempList = Object.assign([], list);
        const partner = await this.getUserOrError(user);
        if (!this.isInList(partner.uid, tempList)) {
            tempList.push(this.transformToDisplayableUserProfile(partner));
        }
        return tempList;
    }

    private isInList(userId: string, list?: IDisplayableUserProfile[]): boolean {
        if (!list) {
            return false;
        }
        const found = list.find((du: IDisplayableUserProfile) => du.uid === userId);
        return found ? true : false;
    }

    private transformToDisplayableUserProfile(user: UserProfile): IDisplayableUserProfile {
        return DisplayableUserProfile.AsDisplayableUserProfile(user);
    }

    private async getUserOrError(user: string | UserProfile): Promise<UserProfile> {
        if (typeof (user) === 'string') {
            const profile = await this.userProfileRepository.get(user);
            if (!profile) {
                throw new HandledError(`No user found with uid ${user}`);
            }
            return profile;
        }
        return user;
    }
}
